import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = {
    class: "flex flex-col flex-1 min-h-0 gap-6",
    "data-testid": "donation-tab-tax-receipt"
};
const _hoisted_2 = { class: "text-lg font-bold" };
const _hoisted_3 = {
    class: "grid grid-cols-3 gap-x-8 gap-y-6",
    "data-testid": "tax-receipt-empty-state"
};
const _hoisted_4 = { class: "flex flex-col gap-2" };
const _hoisted_5 = { class: "text-sm text-(--colors-text-secondary)" };
const _hoisted_6 = { class: "flex flex-col gap-2" };
const _hoisted_7 = { class: "text-sm text-(--colors-text-secondary)" };
const _hoisted_8 = { class: "flex flex-col gap-2" };
const _hoisted_9 = { class: "text-sm text-(--colors-text-secondary)" };
const _hoisted_10 = {
    key: 4,
    class: "flex flex-col gap-4"
};
import { computed, onMounted, ref, watch } from "vue";
import { Badge, Button, WidgetShell } from "@eudonet/opal";
import ModuleLoading from "@/shared/components/ModuleLoading.vue";
import ModuleState from "@/shared/components/ModuleState.vue";
import TaxReceiptCard from "./TaxReceiptCard.vue";
import AnnualTaxReceiptPanel from "./AnnualTaxReceiptPanel.vue";
import TaxReceiptCancelModal from "./TaxReceiptCancelModal.vue";
import { useI18n } from "vue-i18n";
import { useAppToast } from "@/shared/composables/useAppToast";
import { getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import { apiErrorDetail, isTaxReceiptEmailFailureCode, taxReceiptEmailFailureReason, } from "../../../domain/entities/taxReceiptEmailFailure";
import { cancelTaxReceipt, getTaxReceiptHistory, resendTaxReceiptEmail, revokeTaxReceiptPublicLink, } from "../../../infrastructure/api/donationApi";
const PER_PAGE = 20;
export default /*@__PURE__*/ _defineComponent({
    __name: 'DonationTabTaxReceipt',
    props: {
        donationId: {},
        currency: { default: undefined },
        donationType: { default: "OneOff" },
        donationStatus: { default: undefined },
        contactId: { default: undefined },
        organizationId: { default: undefined }
    },
    emits: ["receipt-updated"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        /** A regular donation has no per-donation actions: its receipt is the annual one, read-only here. */
        const isRegularDonation = computed(() => props.donationType === "Regular");
        const emit = __emit;
        const { t } = useI18n();
        const { toastSuccess, toastError } = useAppToast();
        const receipts = ref([]);
        // Reported by the annual panel: the active annual receipt listing this one-off, if any.
        const annualReceipt = ref(null);
        const totalPages = ref(1);
        const currentPage = ref(1);
        const isLoading = ref(false);
        const isLoadingAnnual = ref(false);
        const isLoadingMore = ref(false);
        const loadError = ref(false);
        // The annual read waits for the unit history: a one-off with its own receipt never needs it.
        const historyLoaded = ref(false);
        const resendingId = ref(null);
        const cancellingId = ref(null);
        const revokingLinkId = ref(null);
        const showCancelModal = ref(false);
        const receiptToCancel = ref(null);
        const hasMorePages = computed(() => currentPage.value < totalPages.value);
        const activeUnitReceipt = computed(() => receipts.value.find((r) => r.status !== "Cancelled") ?? null);
        // The parent reads a single receipt: the active unit one when present, else the annual one listing
        // the donation (active or cancelled, OP-7612), else the most recent row — the same collapse
        // GetByDonation applies server-side (OP-5849). The parent's lock guard ignores a Cancelled row,
        // its deletion rule counts it.
        // Every mutation replaces the array wholesale, so a shallow watch is enough.
        watch([receipts, annualReceipt], ([list, annual]) => emit("receipt-updated", activeUnitReceipt.value ?? annual ?? list[0] ?? null));
        const RESEND_ERROR_KEYS = {
            TaxReceiptInvalidTransition: "finance.donations.taxReceipt.resendErrorConflict",
            // No stored PDF: the only DomainRuleViolation the resend still raises (OP-8127).
            DomainRuleViolation: "finance.donations.taxReceipt.resendErrorNoPdf",
        };
        const CANCEL_ERROR_KEYS = {
            TaxReceiptInvalidTransition: "finance.donations.taxReceipt.cancelErrorConflict",
            // The modal blocks these client-side; the mappings cover a direct/raced request anyway.
            "TaxReceipt.CancellationReasonCode.Required": "finance.donations.taxReceipt.cancelModal.motifRequired",
            "TaxReceipt.CancellationReasonCode.Invalid": "finance.donations.taxReceipt.cancelModal.motifInvalid",
            "TaxReceipt.CancellationReason.Required": "finance.donations.taxReceipt.cancelModal.commentRequired",
            "TaxReceipt.CancellationReason.TooLong": "finance.donations.taxReceipt.cancelModal.commentTooLong",
        };
        function errorKey(err, overrides, fallback) {
            const code = getApiErrorCode(err);
            return code && overrides[code] ? overrides[code] : fallback;
        }
        async function loadReceipts() {
            // A regular donation's tab shows the annual panel only; the parent fetches its own lock receipt.
            if (!props.donationId || isRegularDonation.value)
                return;
            annualReceipt.value = null;
            isLoading.value = true;
            loadError.value = false;
            try {
                const page = await getTaxReceiptHistory(props.donationId, 1, PER_PAGE);
                receipts.value = page.items;
                totalPages.value = page.totalPages;
                currentPage.value = 1;
                historyLoaded.value = true;
            }
            catch {
                receipts.value = [];
                loadError.value = true;
            }
            finally {
                isLoading.value = false;
            }
        }
        async function loadMore() {
            if (!hasMorePages.value || isLoadingMore.value)
                return;
            isLoadingMore.value = true;
            try {
                const page = await getTaxReceiptHistory(props.donationId, currentPage.value + 1, PER_PAGE);
                receipts.value = [...receipts.value, ...page.items];
                totalPages.value = page.totalPages;
                currentPage.value += 1;
            }
            catch {
                toastError("finance.donations.taxReceipt.loadError");
            }
            finally {
                isLoadingMore.value = false;
            }
        }
        function handleDownload(receipt) {
            if (receipt.pdfUrl) {
                window.open(receipt.pdfUrl, "_blank", "noopener");
            }
        }
        async function handleResend(receipt) {
            if (resendingId.value)
                return;
            resendingId.value = receipt.taxReceiptId;
            try {
                const updated = await resendTaxReceiptEmail(receipt.taxReceiptId);
                receipts.value = receipts.value.map((r) => (r.taxReceiptId === updated.taxReceiptId ? updated : r));
                toastSuccess(receipt.sentAt ? "finance.donations.taxReceipt.resendSuccess" : "finance.donations.taxReceipt.sendSuccess");
            }
            catch (err) {
                const code = getApiErrorCode(err);
                // Matched on the code, whatever the status (422, 500 or 502): OPDON records every one on the receipt.
                if (isTaxReceiptEmailFailureCode(code)) {
                    // The reason keys are sentence fragments (shared with the card's "Last email attempt failed on
                    // {date}: {reason}"); the toast wraps one in its own sentence.
                    toastError(code === "TaxReceipt.Email.OutcomeUnknown"
                        ? "finance.donations.taxReceipt.resendErrorOutcomeUnknown"
                        : "finance.donations.taxReceipt.resendErrorReason", { reason: taxReceiptEmailFailureReason(t, code, apiErrorDetail(err)) });
                    // A refused journey is recorded on the receipt: reload so the card shows the same reason.
                    await loadReceipts();
                }
                else {
                    toastError(errorKey(err, RESEND_ERROR_KEYS, "finance.donations.taxReceipt.resendError"));
                }
            }
            finally {
                resendingId.value = null;
            }
        }
        async function handleRevokeLink(receipt) {
            if (revokingLinkId.value)
                return;
            revokingLinkId.value = receipt.taxReceiptId;
            try {
                const updated = await revokeTaxReceiptPublicLink(receipt.taxReceiptId);
                receipts.value = receipts.value.map((r) => (r.taxReceiptId === updated.taxReceiptId ? updated : r));
                toastSuccess("finance.donations.taxReceipt.publicLink.revoked");
            }
            catch {
                // A mutation toasts and never writes loadError: that slot replaces the whole card list.
                toastError("finance.donations.taxReceipt.publicLink.revokeFailed");
            }
            finally {
                revokingLinkId.value = null;
            }
        }
        function openCancelModal(receipt) {
            receiptToCancel.value = receipt;
            showCancelModal.value = true;
        }
        async function handleCancel(reasonCode, reason) {
            const target = receiptToCancel.value;
            if (!target || cancellingId.value)
                return;
            cancellingId.value = target.taxReceiptId;
            try {
                await cancelTaxReceipt(target.taxReceiptId, reasonCode, reason);
                showCancelModal.value = false;
                receiptToCancel.value = null;
                toastSuccess("finance.donations.taxReceipt.cancelSuccess");
                // Reload the chain rather than patching in place: cancelling changes which receipt is
                // active, and the fresh page re-signs every pdfUrl.
                await loadReceipts();
            }
            catch (err) {
                toastError(errorKey(err, CANCEL_ERROR_KEYS, "finance.donations.taxReceipt.cancelError"));
            }
            finally {
                cancellingId.value = null;
            }
        }
        onMounted(loadReceipts);
        watch(() => [props.donationId, isRegularDonation.value], loadReceipts);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(WidgetShell), {
                    stretch: "",
                    loading: isLoading.value || isLoadingAnnual.value
                }, {
                    "before-actions": _withCtx(() => [
                        _cache[7] || (_cache[7] = _createElementVNode("span", { class: "ri-file-text-line text-lg text-(--colors-text-secondary)" }, null, -1 /* CACHED */)),
                        _createElementVNode("h2", _hoisted_2, _toDisplayString(_ctx.$t("finance.donations.tabs.taxReceipt")), 1 /* TEXT */)
                    ]),
                    default: _withCtx(() => [
                        (isRegularDonation.value)
                            ? (_openBlock(), _createBlock(AnnualTaxReceiptPanel, {
                                key: 0,
                                "donation-id": __props.donationId,
                                "donation-status": __props.donationStatus,
                                currency: __props.currency,
                                "contact-id": __props.contactId,
                                "organization-id": __props.organizationId,
                                "onUpdate:loading": _cache[0] || (_cache[0] = ($event) => (isLoadingAnnual.value = $event)),
                                onDownload: handleDownload,
                                onReceiptUpdated: _cache[1] || (_cache[1] = ($event) => (emit('receipt-updated', $event)))
                            }, null, 8 /* PROPS */, ["donation-id", "donation-status", "currency", "contact-id", "organization-id"]))
                            : (isLoading.value)
                                ? (_openBlock(), _createBlock(ModuleLoading, { key: 1 }))
                                : (loadError.value)
                                    ? (_openBlock(), _createBlock(ModuleState, {
                                        key: 2,
                                        type: "error",
                                        title: _ctx.$t('finance.donations.taxReceipt.loadError'),
                                        "cta-icon": "ri-refresh-line",
                                        "cta-label": _ctx.$t('finance.donations.taxReceipt.retry'),
                                        "data-testid": "tax-receipt-load-error",
                                        onCta: loadReceipts
                                    }, null, 8 /* PROPS */, ["title", "cta-label"]))
                                    : (historyLoaded.value && receipts.value.length === 0)
                                        ? (_openBlock(), _createBlock(AnnualTaxReceiptPanel, {
                                            key: 3,
                                            "receipts-only": "",
                                            "donation-id": __props.donationId,
                                            "donation-status": __props.donationStatus,
                                            currency: __props.currency,
                                            "onUpdate:loading": _cache[2] || (_cache[2] = ($event) => (isLoadingAnnual.value = $event)),
                                            onDownload: handleDownload,
                                            onReceiptUpdated: _cache[3] || (_cache[3] = ($event) => (annualReceipt.value = $event))
                                        }, {
                                            empty: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_3, [
                                                    _createElementVNode("div", _hoisted_4, [
                                                        _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t("finance.donations.taxReceipt.status")), 1 /* TEXT */),
                                                        _createVNode(_unref(Badge), {
                                                            theme: "red",
                                                            label: _ctx.$t('finance.donations.taxReceipt.statusValue.NotIssued'),
                                                            "show-close-icon": false,
                                                            size: "sm"
                                                        }, null, 8 /* PROPS */, ["label"])
                                                    ]),
                                                    _createElementVNode("div", _hoisted_6, [
                                                        _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.$t("finance.donations.taxReceipt.number")), 1 /* TEXT */),
                                                        _cache[8] || (_cache[8] = _createElementVNode("span", { class: "text-sm text-(--colors-text-default)" }, "—", -1 /* CACHED */))
                                                    ]),
                                                    _createElementVNode("div", _hoisted_8, [
                                                        _createElementVNode("span", _hoisted_9, _toDisplayString(_ctx.$t("finance.donations.taxReceipt.eligibleAmount")), 1 /* TEXT */),
                                                        _cache[9] || (_cache[9] = _createElementVNode("span", { class: "text-sm text-(--colors-text-default)" }, "—", -1 /* CACHED */))
                                                    ])
                                                ])
                                            ]),
                                            _: 1 /* STABLE */
                                        }, 8 /* PROPS */, ["donation-id", "donation-status", "currency"]))
                                        : (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                            (historyLoaded.value && activeUnitReceipt.value === null)
                                                ? (_openBlock(), _createBlock(AnnualTaxReceiptPanel, {
                                                    key: 0,
                                                    "receipts-only": "",
                                                    "donation-id": __props.donationId,
                                                    "donation-status": __props.donationStatus,
                                                    currency: __props.currency,
                                                    "onUpdate:loading": _cache[4] || (_cache[4] = ($event) => (isLoadingAnnual.value = $event)),
                                                    onDownload: handleDownload,
                                                    onReceiptUpdated: _cache[5] || (_cache[5] = ($event) => (annualReceipt.value = $event))
                                                }, null, 8 /* PROPS */, ["donation-id", "donation-status", "currency"]))
                                                : _createCommentVNode("v-if", true),
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(receipts.value, (item) => {
                                                return (_openBlock(), _createBlock(TaxReceiptCard, {
                                                    key: item.taxReceiptId,
                                                    receipt: item,
                                                    currency: __props.currency,
                                                    resending: resendingId.value === item.taxReceiptId,
                                                    cancelling: cancellingId.value === item.taxReceiptId,
                                                    "revoking-link": revokingLinkId.value === item.taxReceiptId,
                                                    onDownload: handleDownload,
                                                    onResend: handleResend,
                                                    onCancel: openCancelModal,
                                                    onRevokeLink: handleRevokeLink
                                                }, null, 8 /* PROPS */, ["receipt", "currency", "resending", "cancelling", "revoking-link"]));
                                            }), 128 /* KEYED_FRAGMENT */)),
                                            (hasMorePages.value)
                                                ? (_openBlock(), _createBlock(_unref(Button), {
                                                    key: 1,
                                                    variant: "tertiary",
                                                    size: "sm",
                                                    class: "self-center",
                                                    "icon-left": "ri-arrow-down-s-line",
                                                    label: _ctx.$t('finance.donations.taxReceipt.showMore'),
                                                    loading: isLoadingMore.value,
                                                    "data-testid": "tax-receipt-show-more",
                                                    onClick: loadMore
                                                }, null, 8 /* PROPS */, ["label", "loading"]))
                                                : _createCommentVNode("v-if", true)
                                        ]))
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["loading"]),
                _createVNode(TaxReceiptCancelModal, {
                    modelValue: showCancelModal.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((showCancelModal).value = $event)),
                    receipt: receiptToCancel.value,
                    cancelling: cancellingId.value !== null,
                    onConfirm: handleCancel
                }, null, 8 /* PROPS */, ["modelValue", "receipt", "cancelling"])
            ]));
        };
    }
});
